import { findStrayDevices } from "../strayDevices";
import type { CablePosition } from "../../findCableGaps";
import type { KarlAssignment, KarlCircuit, KarlSchematicWay } from "../../karlSteps";
import type {
  BuildGroupingWork,
  KarlGroupingItem,
  KarlGroupingParked,
} from "./types";

/**
 * The run writes this in front of a group it bucketed by symbol because it read
 * no identifier beside the devices. "grp·Sirene" is not a circuit — it is every
 * siren on the floor in one bag, and the estimator has to be told that.
 */
const FALLBACK_KEY_PREFIX = "grp·";

const KIND_RANK: Record<KarlGroupingItem["kind"], number> = {
  stray: 0,
  empty_way: 1,
  question: 2,
  unkeyed_group: 3,
};

/** Same rule the schematic lookup uses: "-0F5", "0F5" and "0f5" are one tag. */
const normalize = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const hasReadIdentifier = (circuit: KarlCircuit): boolean => {
  const key = circuit.circuitKey?.trim();
  return Boolean(key) && !key?.startsWith(FALLBACK_KEY_PREFIX);
};

const placedPositions = (circuit: KarlCircuit): CablePosition[] =>
  circuit.devices
    .map((device) => device.position)
    .filter((position): position is CablePosition => position !== null);

const allCircuits = (
  assignments: KarlAssignment[],
): Array<{ circuit: KarlCircuit; sourceName: string }> =>
  assignments.flatMap((assignment) =>
    assignment.circuits.map((circuit) => ({
      circuit,
      sourceName: assignment.sourceName,
    })),
  );

const wayItem = (
  way: KarlSchematicWay,
  tagOnPlan: boolean | null,
): KarlGroupingItem => ({
  id: `way:${way.board}:${way.key}`,
  kind: "empty_way",
  label: way.key,
  description: way.description,
  board: way.board,
  cableType: way.cableType,
  devices: 0,
  positions: [],
  focus: null,
  targetCircuitId: way.circuitIds[0] ?? null,
  wayKey: way.key,
  breaker: way.breaker,
  tagOnPlan,
  why: "",
  stray: null,
  question: null,
});

export const buildGroupingWork: BuildGroupingWork = ({ steps, tags }) => {
  const circuits = allCircuits(steps.assignments);
  const keyed = circuits.filter((entry) => hasReadIdentifier(entry.circuit));
  const unkeyed = circuits.filter((entry) => !hasReadIdentifier(entry.circuit));

  const countDevices = (
    entries: Array<{ circuit: KarlCircuit }>,
  ): number =>
    entries.reduce((total, entry) => total + entry.circuit.devices.length, 0);

  const unkeyedDevices = countDevices(unkeyed);
  const strandedByExtraction = steps.agent.strandedByExtraction;
  // The run says how many devices a missing schedule stranded, never which
  // ones — so the gap between the two counts is what is left to place by hand.
  const unexplained = Math.max(0, unkeyedDevices - strandedByExtraction);

  const ledger = {
    circuitCount: circuits.length,
    deviceCount: countDevices(circuits),
    keyedDevices: countDevices(keyed),
    unkeyedDevices,
    unkeyedGroups: unkeyed.length,
    strandedByExtraction,
    unexplained,
    waysTotal: steps.ways.length,
    waysFilled: steps.ways.filter((way) => way.deviceCount > 0).length,
  };

  const work: KarlGroupingItem[] = [];
  const parked: KarlGroupingParked[] = [];

  for (const group of findStrayDevices({ assignments: steps.assignments })) {
    work.push({
      id: `stray:${group.circuitId}`,
      kind: "stray",
      label: group.circuitKey ?? group.circuitId,
      description: group.cableType,
      board: group.sourceName,
      cableType: group.cableType,
      devices: group.strays.length,
      positions: group.positions,
      focus: group.strays[0].position,
      targetCircuitId: group.circuitId,
      wayKey: group.circuitKey,
      breaker: null,
      tagOnPlan: null,
      why: "",
      stray: group,
      question: null,
    });
  }

  const onPlan = tags ? new Set(tags.onPlan.map(normalize)) : null;
  const notOnPlan: KarlSchematicWay[] = [];
  for (const way of steps.ways) {
    if (way.deviceCount > 0) continue;
    const printed = onPlan ? onPlan.has(normalize(way.key)) : null;
    // A way whose identifier is printed nowhere on this sheet cannot be filled
    // by a box drawn on this sheet — its devices are on a drawing nobody
    // annotated. Listing it as work is what sent estimators hunting.
    if (printed === false) {
      notOnPlan.push(way);
      continue;
    }
    work.push(wayItem(way, printed));
  }
  if (notOnPlan.length) {
    parked.push({
      kind: "way_not_on_plan",
      devices: 0,
      entries: notOnPlan.length,
      subjects: notOnPlan.map((way) => way.key),
      why: "",
    });
  }

  for (const question of steps.agent.questions) {
    if (question.step !== "grouping" || question.kind !== "draw") continue;
    work.push({
      id: `question:${question.id}`,
      kind: "question",
      label: question.subjects[0] ?? "",
      description: "",
      board: "",
      cableType: "",
      devices: question.devices,
      positions: [],
      focus: null,
      targetCircuitId: null,
      wayKey: null,
      breaker: null,
      tagOnPlan: null,
      why: question.why,
      stray: null,
      question,
    });
  }

  const bySize = [...unkeyed].sort(
    (a, b) =>
      b.circuit.devices.length - a.circuit.devices.length ||
      a.circuit.circuitId.localeCompare(b.circuit.circuitId),
  );
  let toPlace = unexplained;
  const explained: typeof bySize = [];
  for (const entry of bySize) {
    if (toPlace <= 0) {
      explained.push(entry);
      continue;
    }
    toPlace -= entry.circuit.devices.length;
    work.push({
      id: `unkeyed:${entry.circuit.circuitId}`,
      kind: "unkeyed_group",
      label: entry.circuit.circuitKey ?? entry.circuit.circuitId,
      description: entry.circuit.cableType,
      board: entry.sourceName,
      cableType: entry.circuit.cableType,
      devices: entry.circuit.devices.length,
      positions: placedPositions(entry.circuit),
      focus: null,
      targetCircuitId: entry.circuit.circuitId,
      wayKey: null,
      breaker: null,
      tagOnPlan: null,
      why: "",
      stray: null,
      question: null,
    });
  }
  if (explained.length) {
    parked.push({
      kind: "explained_group",
      devices: countDevices(explained),
      entries: explained.length,
      subjects: explained.map(
        (entry) => entry.circuit.circuitKey ?? entry.circuit.circuitId,
      ),
      why: "",
    });
  }

  for (const question of steps.agent.questions) {
    if (question.step !== "grouping" || question.kind !== "supply") continue;
    parked.push({
      kind: "missing_schedule",
      devices: question.devices,
      entries: 1,
      subjects: question.subjects,
      why: question.why,
    });
  }

  return {
    ledger,
    work: work.sort(
      (a, b) =>
        KIND_RANK[a.kind] - KIND_RANK[b.kind] ||
        Number(b.tagOnPlan === true) - Number(a.tagOnPlan === true) ||
        b.devices - a.devices ||
        a.id.localeCompare(b.id),
    ),
    parked,
  };
};
